import React, { useState, useEffect } from 'react'
import { useTranslate, useNotify, FormDataConsumer, useRefresh } from 'react-admin'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  TableFooter,
  Skeleton,
  Grid,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  TextField,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  FormControl,
  MenuItem,
  Select,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import { reverseExchangeRate } from 'src/components/pages/payment/payment/logic/common/calculateReverseExchangeRate'
import { useForm, FormProvider, useWatch, useFormContext } from 'react-hook-form'
import { itemServices } from 'src/services/reservation/item/item.service'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import PaymentIcon from '@mui/icons-material/Payment'
import PaymentsIcon from '@mui/icons-material/Payments'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import {
  PERMISSION_PAYMENT,
  PERMISSION_SHOW,
  PERMISSION_EDIT,
  PERMISSION_RECEIPT,
  PERMISSION_UNAPPLY
} from 'src/helper/Permission'
import { extractDetails } from 'src/components/pages/payment/payment/logic/common/extracDetails'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { LoadingButton } from '@mui/lab'
import { getAirports } from 'src/components/pages/sales/reservation/logic/air/AirScaleRow'
import { userServices } from 'src/services/reservation/user/user.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { CANCELLED, EXPIRED, REJECTED, REFUNDED } from 'src/components/pages/sales/reservation/logic/common/state'
import {
  AIR_DETAIL_PERMISSION_ENTITY,
  DUE_DATE_RANGE_PERMISSION_ENTITY,
} from 'src/helper/resourcesByPermission'
import { AIR } from 'src/components/pages/sales/reservation/logic/common/products'
import { styled } from '@mui/material/styles'
import { PaymentTypeArray } from 'src/components/pages/payment/payment/logic/PaymentTypeArray'
import { usePaymentContext } from 'src/components/pages/payment/payment/context/paymentContext'
import { PaymentProviderContext } from 'src/components/pages/payment/payment/context/PaymentProvider'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { paymentServices } from 'src/services/payment/Payment.service'
import { ModalPayAirItem } from 'src/components/pages/sales/reservation/logic/payment/logic/ModalPayAirItem'
import { paymentDetailsByType } from 'src/components/pages/sales/reservation/logic/payment/logic/paymentDetailsByType'
import { getField } from 'src/components/pages/sales/reservation/logic/payment/helper/getField'
import { useErrorHandler } from 'src/hooks/useErrorHandler'
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'


const getBackgroundColor = (status) => {
  if (status === 'warning') {
    return '#FF9800'
  }
  if (status === 'expired') {
    return '#f44336'
  }
  return 'transparent'
}

const StyledTableCell = styled(TableCell)(() => ({
  padding: '2px 6px',
  textAlign: 'center',
  lineHeight: 1.2,
  fontSize: '0.875rem',
  borderRadius: '4px',
}))

const StatusText = styled('span')(({ status }) => ({
  backgroundColor: getBackgroundColor(status),
  color: status !== 'normal' ? '#fff' : 'inherit',
  borderRadius: '4px',
  padding: '0 4px',
  marginRight: '6px',
}))

const TableDueDate = ({ dueDate, amount_pending, sx }) => {
  const translate = useTranslate()
  let status = 'normal'

  if (!dueDate) {
    return <StyledTableCell>-</StyledTableCell>
  }

  const dueDateObj = new Date(dueDate + 'T00:00:00')

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dueYear = dueDateObj.getFullYear()
  const dueMonth = dueDateObj.getMonth()
  const dueDay = dueDateObj.getDate()

  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()
  const todayDay = today.getDate()

  let displayText = dueDateObj.toLocaleDateString()
  let statusText = null
  if (dueYear === todayYear && dueMonth === todayMonth && dueDay === todayDay && amount_pending !== 0) {
    status = 'warning' // Vence hoy
    statusText = translate('ra.status.DUE_TODAY') // Texto para vencer hoy (ajusta según sea necesario)
  } else if (dueDateObj < today && amount_pending !== 0) {
    status = 'expired'
    statusText = `${translate('ra.status.DEFEATED')}`
    displayText = `(${dueDateObj.toLocaleDateString()})`
  }

  return (
    <StyledTableCell sx={sx}>
      {statusText && <StatusText status={status}>{statusText}</StatusText>}
      {displayText}
    </StyledTableCell>
  )
}

const getTotal = (paymentType) => {
  try {
    return paymentType
      .map((payment) => payment?.extra_cost)
      .flat()
      .filter((cost) => cost?.name === 'net_amount')
      .reduce((sum, current) => sum + current?.value, 0)
    // Filtrar solo los objetos con name 'net_amount'
    // Sumar los valores de esos objetos
  } catch (error) {
    return 0
  }
}

const FormWrapperDialogPaymentItem = ({ children, onMethodsChange }) => {
  const methods = useForm({
    defaultValues: {},
  })

  useEffect(() => {
    if (onMethodsChange) {
      onMethodsChange(methods)
    }
  }, [])

  return <FormProvider {...methods}>{children}</FormProvider>
}

const ConfirmButtonToPay = ({
  selectedItems,
  paymentCurrecies,
  hash_business_unit,
  currency,
  onClosePaymentItemModal,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const showError = useErrorHandler()
  const paymentType = useWatch({ name: 'paymentType' })
  const { reset } = useFormContext()
  const [hasAmount, setHasAmount] = useState(false)
  const [loadingGeneratePayment, setLoadingGeneratePayment] = useState(false)
  const [loadingGeneratePaymentWithReceipt, setLoadingGeneratePaymentWithReceipt] = useState(false)

  const areAllExtraCostsSuccess = (payments) => {
    return (
      payments &&
      Array.isArray(payments) &&
      payments?.every(
        (payment) =>
          payment?.extra_cost &&
          Array.isArray(payment?.extra_cost) &&
          payment.extra_cost?.every((cost) => cost?.success === true)
      )
    )
  }

  useEffect(() => {
    if (paymentType) {
      setHasAmount(paymentType.some((item) => item.amount_to_pay > 0) && areAllExtraCostsSuccess(paymentType))
      return
    }
    setHasAmount(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType])

  const onErrorGeneratePayment = (error) => {
    showError(error, () =>
      notify('ra.message.item_payment_generate_error', {
        type: 'error',
      })
    )
    onClosePaymentItemModal()
    reset()
  }

  const onSuccessGeneratePayment = (data) => {
    showError(data, () =>
      notify('ra.message.item_payment_generate_success', {
        type: 'success',
      })
    )
    onClosePaymentItemModal()
    reset()
  }

  const generatePayment = async () => {
    try {
      const body = {
        paymentType: extractDetails(paymentType),
        credit: [],
        currencies_exchange: reverseExchangeRate(paymentCurrecies),
        hash_business_unit: hash_business_unit,
        amount: getTotal(paymentType),
        generate_receipt: false,
        item: selectedItems,
        currency: currency,
      }
      setLoadingGeneratePayment(true)
      const generatePayment = await paymentServices.liquidateExpenses(body)
      if (generatePayment) {
        onSuccessGeneratePayment(generatePayment)
      }
    } catch (error) {
      onErrorGeneratePayment(error)
    } finally {
      setLoadingGeneratePayment(false)
    }
  }

  const generatePaymentWithReceipt = async () => {
    try {
      const body = {
        paymentType: extractDetails(paymentType),
        credit: [],
        currencies_exchange: reverseExchangeRate(paymentCurrecies),
        hash_business_unit: hash_business_unit,
        amount: getTotal(paymentType),
        generate_receipt: true,
        item: selectedItems,
        currency: currency,
      }
      setLoadingGeneratePaymentWithReceipt(true)
      const generatePayment = await paymentServices.liquidateExpenses(body)
      if (generatePayment) {
        onSuccessGeneratePayment(generatePayment)
      }
    } catch (error) {
      onErrorGeneratePayment(error)
    } finally {
      setLoadingGeneratePaymentWithReceipt(false)
    }
  }

  const total_amount_to_pay_item = (selectedItems || []).reduce(
    (total, record) => total + Number(record?.amount_to_pay || 0),
    0
  )

  return (
    <Box flex={1} display={'flex'} justifyContent={'flex-end'}>
      <HasPermission permission={PERMISSION_RECEIPT} resource={'voucher'}>
        <LoadingButton
          className='button'
          variant='contained'
          disabled={
            !hasAmount ||
            !selectedItems.every((item) => item.amount_to_pay) ||
            getTotal(paymentType) - total_amount_to_pay_item != 0
          }
          onClick={generatePaymentWithReceipt}
          loading={loadingGeneratePaymentWithReceipt}
          style={{ margin: '10px' }}
        >
          {translate('ra.action.finish_and_generate_receipt')}
        </LoadingButton>
      </HasPermission>

      <LoadingButton
        className='button'
        variant='contained'
        disabled={
          !hasAmount ||
          !selectedItems.every((item) => item.amount_to_pay) ||
          getTotal(paymentType) - total_amount_to_pay_item != 0
        }
        onClick={generatePayment}
        loading={loadingGeneratePayment}
        style={{ margin: '10px' }}
      >
        {hasPermissionFunction('voucher', PERMISSION_RECEIPT)
          ? translate('ra.action.finish_without_generate_receipt')
          : translate('ra.action.finish')}
      </LoadingButton>
    </Box>
  )
}

const FormWrapperPaymentArray = ({ selectedWorkUnit, selectedBusinessUnit, currency }) => {
  const [rowStyle, setRowStyle] = useState({})
  const { setCurrency } = usePaymentContext()
  const { setValue } = useFormContext()

  useEffect(() => {
    setCurrency(currency)
  }, [currency])

  useEffect(() => {
    setValue('paymentType', [
      {
        payment_type: null,
        id_payment: null,
        currency: currency?.hash_currency,
        apply_extra_cost: true,
        date: new Date(),
        amount_to_pay: null,
      },
    ])
  }, [])

  const [initialValues, setInitialValues] = useState([
    {
      payment_type: null,
      amount_to_pay: null,
      apply_extra_cost: null,
      card: null, // card props
      financial_entity: null, // card props
      quota: null,
    },
  ])
  const [extraCostSuccessStates, setExtraCostSuccessStates] = useState({})

  return (
    <PaymentTypeArray
      selectedWorkUnit={selectedWorkUnit}
      selectedBusinessUnit={selectedBusinessUnit}
      rowStyle={rowStyle}
      setRowStyle={setRowStyle}
      initialValues={initialValues}
      setInitialValues={setInitialValues}
      extraCostSuccessStates={extraCostSuccessStates}
      setExtraCostSuccessStates={setExtraCostSuccessStates}
    />
  )
}


const GeneralActionButton = ({ row, formatItem, reservation, hiddenPayAirItemButtonAndColumn }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [anchorEl, setAnchorEl] = useState(null)
  const [openPaymentAirItemModal, setOpenPaymentAirItemModal] = useState(false)
  const [openDetailsModal, setOpenDetailsModal] = useState(false)
  const [itemSelected, setItemSelected] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [detailsData, setDetailsData] = useState([])
  const [isUnapplyPayment, setIsUnapplyPayment] = useState(false)
  const [loadingUnapplyPayment, setLoadingUnapplyPayment] = useState(false)
  const [loadingGenerateCredit, setLoadinggenerateCredit] = useState(false)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const generateCredit = (hash_item) =>{
    setLoadinggenerateCredit(true)
    paymentServices.generateCredit(hash_item)
    .then(()=>{
      notify('ra.message.credit_generated_success',{type:'success'})
    })
    .catch(()=>{
      notify('ra.message.credit_generated_error',{type:'error'})
    })
    .finally(()=>{
      refresh()
      setTimeout(function() {
        setLoadinggenerateCredit(false)
      }, 5000);
    })
  }

  const disablePayAitItem = (row) => {
    try {
      if (row?.product?.ref_table_detail != AIR) {
        return true
      }
      if (!row?.payment_data?.amount_pending) {
        return true
      }
      return false
    } catch (error) {
      return true
    }
  }

  const handleOpenPayAirItemsModal = async (item) => {
    setItemSelected([formatItem(item)])
    setOpenPaymentAirItemModal(true)
  }

  const toggleDetails = async (item) => {
    setOpenDetailsModal(true)
    setLoadingDetails(true)
    try {
      const response = await paymentServices.getExpensesByItem(item.hash_)
      const enrichedResponse = (response || []).map((detail) => ({
        ...detail,
        item_id: item.item_id,
        details: {
          ...detail?.details,
          amount_used: detail?.amount_used,
        },
      }))
      if (enrichedResponse.length === 0) {
        setDetailsData([{ message: translate('ra.message.no_details_available') }])
      } else {
        setDetailsData(enrichedResponse)
      }
    } catch (error) {
      setDetailsData([{ message: translate('ra.message.error_to_load_details') }])
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleCloseDetailsModal = () => {
    setOpenDetailsModal(false)
    setDetailsData([])
  }

  const renderPaymentDetails = (detail, index) => {
    const fields = paymentDetailsByType[detail.ref_table_detail] || []

    // Calcular el número máximo de columnas basado en campos válidos
    const totalColumns = detailsData.reduce((max, detail) => {
      const fields = paymentDetailsByType[detail.ref_table_detail] || []
      const validFieldsCount = fields.filter((field, fieldIndex) =>
        getField(fieldIndex, detail?.details, field, translate)
      ).length // Contar solo columnas con datos válidos
      return Math.max(max, validFieldsCount)
    }, 0) // tomamos el field que tenga mas columnas, para rellenar fields que tengan menos y que se vea bien

    return (
      <TableRow key={index}>
        {fields.map((field, fieldIndex) => {
          const fieldContent = getField(fieldIndex, detail?.details, field, translate)
          return fieldContent ? (
            <TableCell key={fieldIndex} style={{ fontSize: '0.875rem', padding: '4px' }}>
              <span style={{ display: 'inline' }}>{fieldContent}</span>
            </TableCell>
          ) : null
        })}
        {fields.length < totalColumns &&
          Array.from({
            length: totalColumns - fields.length,
          }).map((_, idx) => <TableCell key={`empty-${idx}`} />)}
      </TableRow>
    )
  }

  const generate_record = async () =>{
    await itemServices.generateRecord(row.hash_).then(()=>{
      refresh()
    })
  }

  const unapply_payment = async () =>{
    setLoadingUnapplyPayment(true)
    await paymentServices.unapplyPayments(row.hash_).then(()=>{
      setIsUnapplyPayment(false)
      notify('ra.message.unapply_payment_success', {
        type: 'success',
      })
      refresh()
    }).finally(()=>{
      setLoadingUnapplyPayment(false)
    }).catch(()=>{
      notify('ra.message.unapply_payment_error', {
        type: 'error',
      })
    })
  }

  return (
    <>
      <TableCell style={{ padding: '0px 16px' }}>
        <Box display={'flex'} alignItems={'center'}>
          {!hiddenPayAirItemButtonAndColumn(reservation?.items) && (
            <HasPermission permission={PERMISSION_PAYMENT} resource={AIR_DETAIL_PERMISSION_ENTITY}>
              <Tooltip
                placement='top'
                title={translate('ra.action.pay_with', {
                  name: translate('ra.field.method_pay_uatp'),
                })}
              >
                <LoadingButton
                  size='small'
                  disabled={disablePayAitItem(row)}
                  onClick={() => {
                    handleOpenPayAirItemsModal(row)
                  }}
                  loading={false}
                  color='primary'
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                  }}
                >
                  <PaymentIcon fontSize='small' />
                </LoadingButton>
              </Tooltip>
            </HasPermission>
          )}
          <IconButton size='small' onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              style: {
                maxHeight: 48 * 4.5,
                width: '20ch',
              },
            }}
          >
            <MenuItem
              onClick={() => {
                toggleDetails(row)
                handleClose()
              }}
            >
              <VisibilityIcon fontSize='small' style={{ marginRight: '8px' }} />
              {translate('ra.action.see_details')}
            </MenuItem>
            <HasPermission resource={'account'} permission='generate_credit'>
              <MenuItem
                disabled = {row.payment_status !== 'CREDIT_NOT_GENERATED' || loadingGenerateCredit}
                onClick={() => {
                  generateCredit(row.hash_)
                }}
              >
                <PaymentIcon fontSize='small' style={{ marginRight: '8px' }} />
                {translate('ra.action.generate_credit')}
              </MenuItem>
            </HasPermission>
            {((row.payment_status === 'PAID' || row.payment_status === 'PARTIALLY_PAID') && hasPermissionFunction('account', PERMISSION_UNAPPLY)) 
              && <MenuItem
              onClick={() => {
                setIsUnapplyPayment(true)
                handleClose()
              }}
            >
              <CreditCardOffIcon fontSize='small' style={{ marginRight: '8px' }} />
              {translate('ra.action.unapply_payment')}
            </MenuItem>}
            {
            (!row?.payment_data || (!row?.payment_data?.currency && !row?.payment_data?.amount_total && !row?.payment_data?.amount_paid && !row?.payment_data?.amount_pending)) &&
              <MenuItem
                onClick={generate_record}
              >
                <LibraryBooksIcon fontSize='small' style={{ marginRight: '8px' }}/>
                {translate('ra.action.generate_record')}
              </MenuItem>
            }
          </Menu>
        </Box>
      </TableCell>
      {openPaymentAirItemModal && (
        <ModalPayAirItem
          openPaymentAirItemModal={openPaymentAirItemModal}
          setOpenPaymentAirItemModal={setOpenPaymentAirItemModal}
          reservation={reservation}
          selectedItems={itemSelected}
          setSelectedItems={setItemSelected}
        />
      )}
      <ConfirmationDialog
          title={translate('ra.message.unapply_payment')}
          buttonConfirmText={translate('ra.action.to_confirm')}
          buttonCancel={translate('ra.action.cancel')}
          setOpen={setIsUnapplyPayment}
          loadingModalButton={loadingUnapplyPayment}
          setConfirm={unapply_payment}
          open={isUnapplyPayment}
        />
      {openDetailsModal && (
        <Dialog fullWidth maxWidth='lg' open={openDetailsModal} onClose={handleCloseDetailsModal}>
          <DialogTitle>{translate('ra.action.details')}</DialogTitle>
          <DialogContent>
            {loadingDetails ? (
              <LinearProgress />
            ) : detailsData.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableBody>
                    {detailsData.map((detail, index) =>
                      detail.message ? (
                        <TableRow key={index}>
                          <TableCell colSpan={2}>
                            <Typography>{detail.message}</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        renderPaymentDetails(detail, index)
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>{translate('ra.message.no_details_available')}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailsModal}>{translate('ra.action.close')}</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export const ItemBalences = ({ reservation }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [paginationItemData, setPaginationItemData] = useState({ page: 0, rowsPerPage: 10 })
  const [paymentCurrecies, setPaymentCurrecies] = useState([])
  const [curreciesByBusinessUnit, setCurreciesByBusinessUnit] = useState([])
  const [currencySelected, setCurrencySelected] = useState({})
  const [loadingBusinessUnitToCurrency, setLoadingBusinessUnitToCurrency] = useState(false)
  const [loadingPaymentCurrencies, setLoadingPaymentCurrencies] = useState(false)
  const [openExchangeModal, setOpenExchangeModal] = useState(false)
  const [loadingItemRecalculate, setLoadingItemRecalculate] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [openPaymentItemModal, setOpenPaymentItemModal] = useState(false)
  const [methods, setMethods] = useState(null)
  const [isChangeExchange, setIsChangeExchange] = useState(false)



  useEffect(() => {
    setSelectAll(
      formatItems(getItemsData())?.length > 0 && selectedItems.length === formatItems(getItemsData()).length
    )
  }, [selectedItems])

  const handleOpenPayItemsModal = async () => {
    const exchange = await getCurrencyByUser()
    getCurrenciesByBusinessUnitById(reservation?.business_unit_relation?.id)
    setPaymentCurrecies(exchange)
    setOpenPaymentItemModal(true)
  }


  const handleItemToPayAmountChange = (e, rowIndex) => {
    const value = e.target.value
    setSelectedItems((prevState) => {
      const updatedData = [...prevState]
      updatedData[rowIndex] = { ...updatedData[rowIndex], amount_to_pay: parseFloat(value) }
      return updatedData
    })
  }

  const formatData = (data) => {
    let auxData = [...data]
    return auxData
      .filter((item) => item?.amount_pending)
      .map((item) => {
        return {
          amount_to_pay: 0,
          item_id: item.item_id,
          id: item.item_id,
          amount_total_converted: item.amount_total_converted,
          amount_pending_converted: item.amount_pending_converted,
          hash_item: item?.hash_item,
          currency_converted: item.currency_converted,
        }
      })
  }

  const applyTotalInStages = (totalAmountToDistribute, selectedItems) => {
    // Si hay solo un item, se aplica el totalAmountToDistribute a ese item
    if (selectedItems.length === 1) {
      const updatedItems = selectedItems.map((item) => {
        item.amount_to_pay = parseFloat(totalAmountToDistribute.toFixed(2))
        return item
      })
      setSelectedItems(updatedItems)
      return
    }

    // Si hay más de un item, se distribuye el monto entre los items
    const updatedItems = selectedItems.map((item, index) => {
      const amountNeeded = item.amount_pending
      if (totalAmountToDistribute > 0) {
        if (totalAmountToDistribute >= amountNeeded) {
          // Si hay suficiente para cubrir la cantidad pendiente
          item.amount_to_pay = parseFloat(amountNeeded.toFixed(2))
          totalAmountToDistribute -= amountNeeded
        } else {
          // Si no hay suficiente para cubrir la cantidad pendiente
          item.amount_to_pay = parseFloat(totalAmountToDistribute.toFixed(2))
          totalAmountToDistribute = 0
        }
      } else {
        // Si ya no hay más monto para distribuir
        item.amount_to_pay = 0
      }
      // Si es el último ítem y todavía hay un totalAmountToDistribute sobrante, se asigna al último ítem
      if (index === selectedItems.length - 1 && totalAmountToDistribute > 0) {
        item.amount_to_pay += parseFloat(totalAmountToDistribute.toFixed(2))
      }

      item.amount_to_pay = parseFloat(item.amount_to_pay.toFixed(2))
      return item
    })

    setSelectedItems(updatedItems)
  }

  const getTotal = (paymentType) => {
    try {
      return paymentType
        .map((payment) => payment?.extra_cost)
        .flat()
        .filter((cost) => cost?.name === 'net_amount')
        .reduce((sum, current) => sum + current?.value, 0)
      // Filtrar solo los objetos con name 'net_amount'
      // Sumar los valores de esos objetos
    } catch (error) {
      return 0
    }
  }

  const onChangeCurrency = (e) => {
    let currency = e.target.value
    if (currency) {
      delete currency.business_unit_relation
      delete currency.business_unit_to_relation
      delete currency.work_unit_relation
      delete currency.work_unit_to_relation
      setCurrencySelected(currency)
      calculateAmountOfItems(
        reservation,
        selectedItems,
        paymentCurrecies,
        currency?.hash_currency,
        currency?.hash_currency != undefined
      )
    } else {
      setCurrencySelected({})
    }
  }

  const handleSelectItem = (item, checked) => {
    let selectedItemsAux = [...selectedItems]
    if (checked) {
      selectedItemsAux.push(formatItem(item))
    } else {
      selectedItemsAux = selectedItemsAux.filter((elem) => elem.item_id !== item.item_id)
    }
    setSelectedItems(selectedItemsAux)
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectAll(checked)
    if (checked) {
      setSelectedItems(formatItems(getItemsData()))
    } else {
      setSelectedItems([])
    }
  }

  const calculateAmountOfItems = async (
    reservation,
    selectedCreditItemsToCreditData,
    paymentCurrecies,
    currency_hash,
    isPaymentItem = false
  ) => {
    try {
      setLoadingItemRecalculate(true)
      const currencies_with_reverse = reverseExchangeRate(paymentCurrecies)
      let items = await itemServices.item_to_pay([reservation?.id], currency_hash, currencies_with_reverse)
      if (items) {
        const item_with_amount_pending_converted = formatData(items)
        const mergedList = (isPaymentItem ? selectedItems : selectedCreditItemsToCreditData).map((item1) => {
          const match = item_with_amount_pending_converted.find((item2) => item2.item_id === item1.item_id)
          if (match) {
            return {
              ...item1,
              amount_total_converted: match.amount_total_converted,
              amount_pending_converted: match.amount_pending_converted,
            }
          }
          return item1
        })
        if (isPaymentItem) {
          // determinar si es el flujo para pagar un item o pagar con credito
          setSelectedItems(mergedList)
        }
      }
    } catch (error) {
      console.log('error', error)
      notify('ra.message.reservation_item_not_found', {
        type: 'error',
      })
    } finally {
      setLoadingItemRecalculate(false)
    }
  }

  const format = (row) => ({
    code: row?.code,
    currency: row?.payment_data?.currency,
    amount_pending: row?.payment_data?.amount_pending,
    product: row?.product,
    passes: row?.passes,
    name: row?.name,
    provider: row?.provider,
    destination: row?.destination,
    pickup_origin: row?.pickup_origin,
    segments: row?.segments,
    item_id: row?.item_id,
    account_detail_relation: row?.payment_data?.account_detail_relation,
    hash_: row?.hash_,
    hash_item: row?.hash_,
    ...addPaxItemInFormatItem(row),
  })

  const addPaxItemInFormatItem = (row) => ({
    pax_relation: row?.pax_relation?.map((pax_relation) => formatPaxItem(pax_relation)),
  })

  const formatPaxItem = (pax_relation) => ({
    pax_id: pax_relation?.id,
    name: pax_relation?.name,
    surname: pax_relation?.surname,
  })

  const formatItems = (item) => {
    try {
      let auxitem = [...item]
      return auxitem?.filter((row) => row?.payment_data?.amount_pending)?.map((row) => format(row))
    } catch (error) {
      return []
    }
  }

  const formatItem = (item) => {
    try {
      return format(item)
    } catch (error) {
      return {}
    }
  }

  const getCurrencyByUser = async () => {
    try {
      setLoadingPaymentCurrencies(true)
      const currencies = await userServices.getCurrencyByUser()
      if (currencies) {
        const format = currencies.filter((currency) => !currency?.is_reverse)
        return format
      } else {
        return []
      }
    } catch (error) {
      return []
    } finally {
      setLoadingPaymentCurrencies(false)
    }
  }

  const onPageItemChange = (number) => {
    const paginationItemDataAux = { ...paginationItemData }
    paginationItemDataAux.page = number
    setPaginationItemData(paginationItemDataAux)
  }

  const onRowsPerPageItemChange = (e) => {
    const paginationItemDataAux = { ...paginationItemData }
    paginationItemDataAux.rowsPerPage = parseInt(e.target.value, 10)
    paginationItemDataAux.page = 0
    setPaginationItemData(paginationItemDataAux)
  }

  const getProductName = (item) => {
    if (item?.product?.ref_table_detail === 'hotel_detail') {
      return item?.name
    } else if (item?.product?.ref_table_detail === 'car_detail') {
      return item?.provider
    } else if (
      item?.product?.ref_table_detail === 'assistance_detail' ||
      item?.product?.ref_table_detail === 'other_detail'
    ) {
      return `${item.destination} / ${item.name}`
    } else if (item?.product?.ref_table_detail === 'transfer_detail') {
      return item?.pickup_origin
    } else if (item?.product?.ref_table_detail === 'air_detail') {
      return getAirports(item?.segments?.[0]?.itineraryGroup)
    } else if (item?.product?.ref_table_detail === 'train_detail_segment') {
      return `${item?.segments?.[0]?.origin_name}/${item?.segments?.[0]?.arrival_name}`
    } else if (item?.product?.ref_table_detail === 'train_detail_passes') {
      return item?.passes?.[0]?.company_name
    }
  }

  const getItemsData = () => {
    return paginationItemData.rowsPerPage > 0
      ? reservation?.items
          ?.filter((item) => item?.status !== REJECTED && item?.status !== EXPIRED)
          ?.slice(
            paginationItemData.page * paginationItemData.rowsPerPage,
            paginationItemData.page * paginationItemData.rowsPerPage + paginationItemData.rowsPerPage
          )
      : reservation?.filter((item) => item?.status !== REJECTED && item?.status !== EXPIRED)?.items
  }

  const getCurrencies = () => {
    const currencies = Array.from(
      new Set(
        reservation?.items
          .map(
            (elem) => elem?.pricing_relation?.[0]?.currency || elem?.pricing_relation?.[0]?.currency_origin
          )
          .filter((elem) => elem)
      )
    )
    return currencies
  }

  const getCurrenciesByBusinessUnitById = async (selectedBusinessUnitId) => {
    try {
      if (!selectedBusinessUnitId) {
        notify('ra.message.has_not_business_unit_to_get_currencies', {
          type: 'error',
        })
        setCurreciesByBusinessUnit([])
        return
      }
      setLoadingBusinessUnitToCurrency(true)
      const currencies = await businessUnitServices.currencies_by_business_unit(selectedBusinessUnitId)
      if (currencies) {
        setCurreciesByBusinessUnit(currencies)
      } else {
        notify('ra.message.has_not_currencies_by_business_unit', {
          type: 'error',
        })
        setCurreciesByBusinessUnit([])
      }
    } catch (error) {
      notify('ra.message.has_not_currencies_by_business_unit', {
        type: 'error',
      })
      setCurreciesByBusinessUnit([])
    } finally {
      setLoadingBusinessUnitToCurrency(false)
    }
  }

  const getTotalCurrency = (field, currency) => {
    return reservation?.items
      ?.filter((item) => 
          item?.status !== REJECTED 
          && item?.status !== EXPIRED 
          /* && item?.status !== CANCELLED */ 
          /* && item?.status !== REFUNDED */
        )
      .filter(
        (elem) =>
          elem?.pricing_relation?.[0]?.currency_origin === currency ||
          elem?.pricing_relation?.[0]?.currency === currency
      )
      .reduce((acc, current) => {
        return acc + (current?.payment_data?.[field] || 0)
      }, 0)
  }

  const onClosePaymentItemModal = () => {
    setCurrencySelected({})
    setOpenPaymentItemModal(false)
    setSelectedItems([])
    if (methods) {
      methods.reset()
      setMethods(null)
    }
  }


  const hiddenPayAirItemButtonAndColumn = (items) => {
    try {
      return items.every((item) => item?.product?.ref_table_detail !== AIR)
    } catch (error) {
      return true
    }
  }

  const handleCurrencyChange = (index, field, value) => {
    setIsChangeExchange(true)
    const updatedCurrencies = [...paymentCurrecies]
    updatedCurrencies[index] = { ...updatedCurrencies[index], [field]: parseFloat(value) }
    setPaymentCurrecies(updatedCurrencies)
  }

  const onCloseExhangeModal = () => {
    setOpenExchangeModal(false)
    isChangeExchange &&
      calculateAmountOfItems(
        reservation,
        selectedItems,
        paymentCurrecies,
        currencySelected?.hash_currency,
        currencySelected?.hash_currency != undefined
      )
    setIsChangeExchange(false)
  }

  const DisableStyle = (row) => {
      return { opacity: (row?.payment_data?.amount_total === 0 || row?.status === 'CANCELLED' || row?.status === 'VOID') ? 0.5 : 1 }
  }

  return (
    <>
      <ShowCard
        title={translate('ra.title.item_balances')}
        actionButtonJustify='flex-end'
        actionButtonText={translate('ra.action.pay')}
        actionButtonStartIcon={<PaymentsIcon />}
        actionButtonStyle={{ width: 'fit-content' }}
        actionButtonDisabled={!selectedItems?.length}
        onClickButton={async () => handleOpenPayItemsModal()}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell size='small'>
                  <Checkbox
                    indeterminate={
                      selectedItems.length > 0 && selectedItems.length < formatItems(getItemsData())?.length
                    }
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{ padding: 0, width: 18 }}
                    disabled={!formatItems(getItemsData())?.length}
                  />
                </TableCell>
                <TableCell>{translate('ra.field.type')}</TableCell>
                <TableCell>{translate('ra.field.item_descripcion')}</TableCell>
                <TableCell>{translate('ra.field.locator')}</TableCell>
                <TableCell>{translate('ra.field.currency')}</TableCell>
                <TableCell>{translate('ra.field.amount')}</TableCell>
                <TableCell>{translate('ra.field.amount_paid')}</TableCell>
                <TableCell>{translate('ra.field.amount_pending')}</TableCell>
                {hasPermissionFunction(DUE_DATE_RANGE_PERMISSION_ENTITY, PERMISSION_SHOW) && (
                  <TableCell>{translate('ra.field.due_date')}</TableCell>
                )}
                <TableCell
                  sx={{
                    padding: '0px 8px',
                    width: '30px',
                    textAlign: 'center',
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {React.Children.toArray(
                getItemsData()?.map((row) => (
                  <>
                    <TableRow>
                      <TableCell size='small'>
                        <Checkbox
                          style={{ padding: 0, width: 18 }}
                          onClick={(e) => handleSelectItem(row, e.target.checked)}
                          checked={selectedItems.find((elem) => elem.item_id === row?.item_id) ? true : false}
                          disabled={
                            !row?.payment_data?.amount_pending || 
                            row.payment_status === 'NOT_REQUIRED' ||
                            row.payment_status === 'CREDIT_GENERATED' ||
                            row.payment_status === 'CREDIT_NOT_GENERATED'
                          }
                        />
                      </TableCell>
                      <TableCell sx={DisableStyle(row)}>
                        {row?.product?.product_type_relation?.long_name}
                      </TableCell>
                      <TableCell sx={DisableStyle(row)}>{((row?.status === 'AFTER_SALES' || row?.status === 'AFTER_SALES_HIDDEN')? 'PV - ': '') + getProductName(row)}</TableCell>
                      <TableCell sx={DisableStyle(row)}>{row?.code}</TableCell>
                      <TableCell sx={DisableStyle(row)}>{row?.payment_data?.currency}</TableCell>
                      <TableCell sx={DisableStyle(row)}>
                        {numberFormatterWithDecimalAndThousand(row?.payment_data?.amount_total)}
                      </TableCell>
                      <TableCell sx={DisableStyle(row)}>
                        {numberFormatterWithDecimalAndThousand(row?.payment_data?.amount_paid)}
                      </TableCell>
                      <TableCell sx={DisableStyle(row)}>
                        {numberFormatterWithDecimalAndThousand(row?.payment_data?.amount_pending)}
                      </TableCell>

                      {hasPermissionFunction(DUE_DATE_RANGE_PERMISSION_ENTITY, PERMISSION_SHOW) && (
                        <TableDueDate
                          sx={DisableStyle(row)}
                          dueDate={row?.due_date_final}
                          amount_pending={row?.payment_data?.amount_pending}
                        />
                      )}

                      <GeneralActionButton
                        row={row}
                        formatItem={formatItem}
                        reservation={reservation}
                        hiddenPayAirItemButtonAndColumn={hiddenPayAirItemButtonAndColumn}
                      />
                    </TableRow>
                  </>
                ))
              )}
              {getItemsData()?.length !== 0 &&
                getCurrencies().map((currency, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4} />
                    <TableCell>
                      <b>Total {currency}</b>
                    </TableCell>
                    <TableCell>
                      {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_total', currency))}
                    </TableCell>
                    <TableCell>
                      {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_paid', currency))}
                    </TableCell>
                    <TableCell>
                      {numberFormatterWithDecimalAndThousand(getTotalCurrency('amount_pending', currency))}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={reservation?.items?.length}
                  rowsPerPage={paginationItemData.rowsPerPage}
                  page={paginationItemData.page}
                  onPageChange={(e, number) => onPageItemChange(number)}
                  onRowsPerPageChange={(e) => onRowsPerPageItemChange(e)}
                  labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </ShowCard>

      {openPaymentItemModal && (
        <FormWrapperDialogPaymentItem onMethodsChange={setMethods}>
          <Dialog fullWidth maxWidth='lg' open={openPaymentItemModal} onClose={onClosePaymentItemModal}>
            <DialogTitle>{translate('ra.title.payment_register')}</DialogTitle>

            <DialogContent>
              <Grid container item>
                <Grid item md={8} lg={8}>
                  <Box style={{ margin: '20px' }}>
                    <EditInputsWrap label={translate(`ra.field.currency`)} md={8} lg={8} required>
                      {loadingBusinessUnitToCurrency ? (
                        <Skeleton variant='rectangular' width={300} height={40} />
                      ) : (
                        <FormControl fullWidth>
                          <Select
                            labelId='currencies'
                            id='currencies'
                            value={currencySelected}
                            onChange={onChangeCurrency}
                            size='small'
                          >
                            {curreciesByBusinessUnit.map((currency, i) => (
                              <MenuItem key={i} value={currency}>
                                {currency?.symbol} - {currency?.long_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </EditInputsWrap>
                  </Box>
                </Grid>
                <Grid item md={4} lg={4}>
                  {paymentCurrecies.length > 0 && (
                    <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                      <Box className='currency_resume'>
                        {loadingPaymentCurrencies
                          ? Array.from(new Array(3)).map((_, i) => (
                              <Box key={i} mb={2} display={'flex'}>
                                <Skeleton variant='text' width={30} height={10} /> -
                                <Skeleton variant='text' width={30} height={10} />:
                                <Skeleton variant='text' width={30} height={10} />
                              </Box>
                            ))
                          : paymentCurrecies.map((currency, i) => (
                              <Box key={i}>
                                <Typography component='span' fontWeight='bold'>
                                  {currency?.currency_relation?.short_name}-
                                  {currency?.currency_to_relation?.short_name} : {currency.value}
                                </Typography>
                              </Box>
                            ))}
                      </Box>
                      {hasPermissionFunction('currency-by-work-unit', PERMISSION_EDIT) && (
                        <Box display={'flex'}>
                          <Button
                            onClick={() => setOpenExchangeModal(true)}
                            disabled={!Object.keys(currencySelected)?.length}
                          >
                            <EditIcon />
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Divider variant='fullWidht' />
              {currencySelected?.hash_currency && (
                <>
                  <Grid container item>
                    <Grid item style={{ margin: '30px 0 30px 0' }}>
                      <PaymentProviderContext>
                        <FormWrapperPaymentArray
                          selectedBusinessUnit={reservation?.business_unit_relation}
                          selectedWorkUnit={reservation?.work_unit_relation}
                          currency={currencySelected}
                        />
                      </PaymentProviderContext>
                    </Grid>
                  </Grid>

                  <Divider variant='fullWidht' />
                </>
              )}

              <Grid container item marginY={'20px'}>
                <Grid item md={8} lg={8}>
                  <FormDataConsumer>
                    {({ formData }) => {
                      if (formData?.paymentType) {
                        const total_amount_to_pay_payment_type = getTotal(formData?.paymentType)

                        if (total_amount_to_pay_payment_type) {
                          return (
                            <>
                              {/* se quito provisoriamente ya que puede servir en un futuro (eli y pablo no lo necesitan) */}
                              {/* <LoadingButton  
                                size='small'
                                onClick={() =>
                                  applyTotalEqually(total_amount_to_pay_payment_type, selectedItems)
                                }
                                disabled={null}
                                loading={null}
                                startIcon={<AttachMoneyIcon fontSize='small' />}
                                color='primary'
                              >
                                {translate('ra.field.apply_total_equitably')}
                              </LoadingButton> */}
                              <LoadingButton
                                size='small'
                                onClick={() =>
                                  applyTotalInStages(total_amount_to_pay_payment_type, selectedItems)
                                }
                                disabled={null}
                                loading={null}
                                startIcon={<AttachMoneyIcon fontSize='small' />}
                                color='primary'
                              >
                                {translate('ra.field.apply_total_in_order')}
                              </LoadingButton>
                            </>
                          )
                        }
                      }
                    }}
                  </FormDataConsumer>
                </Grid>
                <Grid item md={4} lg={4}>
                  <FormDataConsumer>
                    {({ formData }) => {
                      if (formData?.paymentType) {
                        const total_amount_to_pay_payment_type = getTotal(formData?.paymentType)

                        const total_amount_to_pay_item = (selectedItems || []).reduce(
                          (total, record) => total + Number(record?.amount_to_pay || 0),
                          0
                        )

                        return (
                          <Box display={'flex'} flexDirection={'column'}>
                            <Typography variant='h6' component='h6'>
                              {translate('ra.field.total')} {currencySelected?.short_name} :{' '}
                              {numberFormatterWithDecimalAndThousand(total_amount_to_pay_payment_type)}
                            </Typography>
                            <Typography
                              variant='h6'
                              component='h6'
                              style={{
                                color:
                                  total_amount_to_pay_payment_type - total_amount_to_pay_item < 0
                                    ? 'red'
                                    : 'inherit',
                              }}
                            >
                              {translate('ra.field.amount_to_apply')} {currencySelected?.short_name} :{' '}
                              {numberFormatterWithDecimalAndThousand(
                                total_amount_to_pay_payment_type - total_amount_to_pay_item
                              )}
                            </Typography>
                          </Box>
                        )
                      }
                    }}
                  </FormDataConsumer>
                </Grid>
              </Grid>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate('ra.field.item_descripcion')}</TableCell>
                      <TableCell>{translate('ra.field.locator')}</TableCell>
                      <TableCell>{translate('ra.field.currency')}</TableCell>
                      <TableCell>{translate('ra.field.amount_pending')}</TableCell>
                      {currencySelected?.hash_currency && (
                        <>
                          <TableCell>
                            {translate('ra.field.amount_pending_in', {
                              currency: currencySelected.short_name,
                            })}
                          </TableCell>
                          <TableCell>
                            {translate('ra.field.amount_to_apply', {
                              currency: currencySelected.short_name,
                            })}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {false ? (
                      <TableRow>
                        {[...Array(5)].map((_, i) => (
                          <TableCell key={i}>
                            <Skeleton variant='rectangular' width={'100%'} height={20} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ) : (
                      <>
                        {selectedItems.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>{getProductName(row)}</TableCell>
                            <TableCell>{row?.code}</TableCell>
                            <TableCell>{row?.currency}</TableCell>
                            <TableCell>
                              {numberFormatterWithDecimalAndThousand(row?.amount_pending)}
                            </TableCell>
                            {currencySelected?.hash_currency && (
                              <>
                                <TableCell>
                                  {loadingItemRecalculate ? (
                                    <>
                                      <Skeleton variant='rectangular' width={60} height={20} />
                                    </>
                                  ) : (
                                    numberFormatterWithDecimalAndThousand(row?.amount_pending_converted)
                                  )}
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    type='number'
                                    value={row?.amount_to_pay || 0}
                                    onChange={(e) => handleItemToPayAmountChange(e, i)}
                                    size='small'
                                  />
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={onClosePaymentItemModal}>
                {translate('ra.action.cancel')}
              </Button>
              <ConfirmButtonToPay
                selectedItems={selectedItems}
                paymentCurrecies={paymentCurrecies}
                hash_business_unit={reservation?.business_unit_relation?.hash_business_unit}
                currency={currencySelected}
                onClosePaymentItemModal={onClosePaymentItemModal}
              />
            </DialogActions>
          </Dialog>
        </FormWrapperDialogPaymentItem>
      )}

      {openExchangeModal && (
        <Dialog fullWidth open={openExchangeModal} onClose={onCloseExhangeModal}>
          <DialogTitle>{translate('ra.entity.payment-entity')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              {paymentCurrecies.map((currency, i) => (
                <Grid item xs={12} key={i}>
                  <Box display='flex' alignItems='center'>
                    <Typography component='span' fontWeight='bold'>
                      {currency?.currency_relation?.short_name}-{currency?.currency_to_relation?.short_name} :
                    </Typography>
                    <TextField
                      defaultValue={currency.value}
                      onChange={(e) => handleCurrencyChange(i, 'value', e.target.value)}
                      size='small'
                      type='number'
                      sx={{ ml: 2, width: '140px' }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={onCloseExhangeModal}>
              {translate('ra.action.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
